/**
 * Клиенты.
 *
 * Вопросы: насколько выручка зависит от нескольких клиентов; как ведут себя
 * крупнейшие за год; кто пришёл впервые; кто покупал и перестал.
 *
 * «Новый» — есть выручка в периоде и не было ни рубля за 12 месяцев до него.
 * «Перестал покупать» — была выручка в прошлом периоде такой же длины и нет
 * в текущем. Оба определения написаны в подписи блока, а не угадываются.
 */

import { type CSSProperties } from 'react'
import { useFinance } from '../../finance/context'
import { num, rowKey, useReport, useSettled, type ReportState } from '../../finance/data'
import { SegmentBar } from '../../finance/charts'
import { Caveat, DataNotes, Delta, Kpi, Panel, SkeletonRows } from '../../finance/parts'
import { originFrom } from '../../finance/Drawer'
import { usePublishFreshness } from '../../finance/freshness'
import { compareLabel, labelOf, monthLabel, monthsOf, previousOf, shortMonth, type DateRange } from '../../finance/period'
import { hasMissingMonths, missingOf, recentWindows } from '../../finance/coverage'
import { salesEmpty } from '../../finance/frame'
import { money } from '../../finance/sections/Overview'
import { deltaPercent, formatPercent, pluralize } from '../../format'
import { MiniColumns, ValueLegend } from '../charts'
import { labelOr, ranked } from '../parts'

const TIERS = [
  { key: 't1', label: 'Крупнейший', from: 0, to: 1, color: 'var(--age-4)' },
  { key: 't5', label: '2–5-й', from: 1, to: 5, color: 'var(--age-3)' },
  { key: 't10', label: '6–10-й', from: 5, to: 10, color: 'var(--age-2)' },
  { key: 't20', label: '11–20-й', from: 10, to: 20, color: 'var(--age-1)' },
  { key: 'rest', label: 'Остальные', from: 20, to: Infinity, color: 'var(--age-0)' },
] as const

function isoDay(date: Date) {
  return date.toISOString().slice(0, 10)
}

/** 12 месяцев, которые закончились накануне периода. */
function historyBefore(range: DateRange): DateRange {
  const start = new Date(`${range.from}T00:00:00Z`)
  const from = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() - 12, 1))
  const to = new Date(start.getTime() - 86_400_000)
  return { from: isoDay(from), to: isoDay(to) }
}

function useCustomersBy(range: DateRange | null, groupBy: string[], metrics: string[], filters?: Record<string, string[]>): ReportState {
  const { scoped } = useFinance()
  return useReport(
    'abc_customers',
    range ? scoped('abc_customers', { period: range, group_by: groupBy, metrics, filters, order_by: { key: 'revenue', direction: 'desc' } }) : null,
  )
}

export function Customers() {
  const live = useFinance()
  const currentLive = useCustomersBy(live.range, ['counterparty'], ['revenue'])
  const beforeLive = useCustomersBy(live.previousRange, ['counterparty'], ['revenue'])
  const historyLive = useCustomersBy(historyBefore(live.range), ['counterparty'], ['revenue'])
  usePublishFreshness([currentLive, historyLive])

  // Новые и ушедшие клиенты считаются сравнением трёх ответов — только
  // когда все три относятся к одному выбору; до тех пор на экране прежние.
  const settled = useSettled([currentLive, beforeLive, historyLive], live)
  const [current, before, history] = settled.states
  const ctx = settled.view
  const { range, previousRange, selection, scopeLabel, drill, coverage, gap } = ctx

  const rows = ranked(current.data?.rows)
  const total = rows.reduce((sum, row) => sum + num(row.revenue), 0)
  const top5 = rows.slice(0, 5)
  const topKeys = top5.map((row) => rowKey(row, 'counterparty')).filter(Boolean)
  const trendLive = useCustomersBy(topKeys.length > 0 ? ctx.trend : null, ['period', 'counterparty'], ['revenue'], { counterparty: topKeys })
  const trendSettled = useSettled([trendLive], { trend: ctx.trend, range })
  const [trendState] = trendSettled.states
  const trend = trendSettled.view.trend

  const previousRows = ranked(before.data?.rows)
  const currentKeys = new Set(rows.map((row) => rowKey(row, 'counterparty')))
  const historyKeys = new Set(ranked(history.data?.rows).map((row) => rowKey(row, 'counterparty')))
  const historyNarrowed = history.data?.meta.period_applied.narrowed ?? false
  // «Новым» можно назвать клиента, только если все 12 месяцев до периода
  // загружены и открыты: иначе новым окажется и давний покупатель.
  const historyGap = historyNarrowed || hasMissingMonths(historyBefore(range), coverage)
  // «Перестал покупать» — только если прошлый период загружен целиком,
  // а текущий вообще есть: пустой месяц выгрузки — не уход клиентов.
  const lostUnknown = Boolean(gap) || hasMissingMonths(previousRange, coverage)
  const fresh = history.status === 'ready' && !historyGap ? rows.filter((row) => !historyKeys.has(rowKey(row, 'counterparty'))) : []
  const freshRevenue = fresh.reduce((sum, row) => sum + num(row.revenue), 0)
  const lost = before.status === 'ready' && !lostUnknown ? previousRows.filter((row) => !currentKeys.has(rowKey(row, 'counterparty'))) : []
  const lostRevenue = lost.reduce((sum, row) => sum + num(row.revenue), 0)

  const tiers = TIERS.map((tier) => {
    const slice = rows.slice(tier.from, tier.to === Infinity ? undefined : tier.to)
    return { ...tier, value: slice.reduce((sum, row) => sum + num(row.revenue), 0), count: slice.length }
  }).filter((tier) => tier.count > 0)
  const top10Share = total > 0 ? (rows.slice(0, 10).reduce((sum, row) => sum + num(row.revenue), 0) / total) * 100 : null
  let running = 0
  let halfCount = 0
  for (const row of rows) {
    running += num(row.revenue)
    halfCount += 1
    if (running >= total / 2) break
  }

  const months = monthsOf(trend)
  const rangeMonths = new Set(monthsOf(trendSettled.view.range))
  const trendByClient = new Map<string, Map<string, number>>()
  for (const row of trendState.data?.rows ?? []) {
    const key = rowKey(row, 'counterparty')
    const map = trendByClient.get(key) ?? new Map<string, number>()
    map.set(String(row.period).slice(0, 10), num(row.revenue))
    trendByClient.set(key, map)
  }
  const trendMax = Math.max(1, ...[...trendByClient.values()].flatMap((map) => [...map.values()]))
  const sumMonths = (map: Map<string, number> | undefined, list: string[]) => list.reduce((acc, month) => acc + (map?.get(month) ?? 0), 0)
  const windows = recentWindows(months, coverage)

  const previousLabel = labelOf(previousOf(selection))
  const openCustomer = (row: Record<string, unknown>, element: HTMLElement) =>
    drill({ kind: 'customer', value: rowKey(row, 'counterparty'), label: labelOr(row.counterparty, 'Клиент не указан') }, originFrom(element))

  return (
    <div className="fin-stack insights">
      {ctx.partial && <Caveat tone="warn">{ctx.partial}</Caveat>}
      <div className="kpi-grid">
        <Kpi
          index={0}
          state={current.status}
          label="Клиентов с покупками"
          value={gap ? null : rows.length}
          format={(value) => String(Math.round(value))}
          memoryKey="cus-count"
          delta={
            ctx.noCompare ? (
              <span className="muted">{ctx.noCompare}</span>
            ) : (
              <Delta value={before.status === 'ready' ? deltaPercent(rows.length, previousRows.length) : null} label={compareLabel(selection)} />
            )
          }
          hint={scopeLabel}
        />
        <Kpi
          index={1}
          state={current.status}
          label="Доля 10 крупнейших"
          value={rows.length > 10 ? top10Share : null}
          format={(value) => formatPercent(value)}
          memoryKey="cus-top10"
          hint={
            rows.length === 0
              ? gap ? 'нет данных за период' : 'В периоде нет продаж'
              : rows.length > 10
              ? `Половину выручки дают ${pluralize(halfCount, 'клиент', 'клиента', 'клиентов')} из ${rows.length}`
              : `Всего ${pluralize(rows.length, 'клиент', 'клиента', 'клиентов')} — доля десяти крупнейших равна 100%`
          }
        />
        <Kpi
          index={2}
          state={historyGap ? 'forbidden' : history.status === 'ready' ? current.status : history.status}
          closedLabel={historyNarrowed ? undefined : 'Не посчитать'}
          closedText={
            historyNarrowed
              ? 'Права открывают не всю историю: «новым» оказался бы и старый клиент'
              : 'Загружены не все 12 месяцев до периода: «новым» оказался бы и старый клиент'
          }
          label="Новые клиенты"
          value={fresh.length}
          format={(value) => String(Math.round(value))}
          memoryKey="cus-new-count"
          hint={`${money(freshRevenue)} выручки · не покупали 12 месяцев до периода`}
        />
        <Kpi
          index={3}
          state={lostUnknown ? 'forbidden' : before.status === 'ready' ? current.status : before.status}
          closedLabel="Не посчитать"
          closedText={gap ? 'За период нет данных — уход клиентов не определить' : `Период «${previousLabel}» загружен не целиком`}
          label="Перестали покупать"
          value={lost.length}
          format={(value) => String(Math.round(value))}
          memoryKey="cus-lost-count"
          tone={total > 0 && lostRevenue / total > 0.1 ? 'bad' : undefined}
          hint={`${money(lostRevenue)} их выручки в периоде «${previousLabel}»; в этом — ни одной покупки${
            selection.grain === 'month' ? '. За месяц это часто пауза, а не уход — сравните кварталы' : ''
          }`}
        />
      </div>

      {gap ? (
        // За период нет выгрузки: один блок с причиной, а не пять одинаковых.
        <Panel index={1} title="Клиенты за период" states={[current]} skeleton={<SkeletonRows rows={3} height={18} />} empty={salesEmpty(ctx, true)}>
          {() => null}
        </Panel>
      ) : (
        <>
      <Panel
        index={1}
        title="Концентрация выручки"
        subtitle="Выручка без НДС по клиентам, от крупнейшего к мелким · цвет темнее — клиент крупнее"
        states={[current]}
        skeleton={<SkeletonRows rows={3} height={18} />}
        empty={salesEmpty(ctx, rows.length === 0, 'Продаж клиентам за период нет')}
      >
        {() => (
          <div className="stack" style={{ gap: 16 }}>
            <div className="ageing-head">
              <span className="strong">
                {rows[0] ? `${labelOr(rows[0].counterparty, 'Клиент не указан')} — ${formatPercent((num(rows[0].revenue) / total) * 100)} выручки` : ''}
              </span>
              <span className="small muted">
                всего {money(total)} от {pluralize(rows.length, 'клиента', 'клиентов', 'клиентов')}
              </span>
            </div>
            <SegmentBar
              ariaLabel="Доли выручки по группам клиентов"
              segments={tiers.map((tier) => ({ key: tier.key, label: tier.label, value: tier.value, color: tier.color }))}
            />
            <ValueLegend
              total={total}
              items={tiers.map((tier) => ({
                key: tier.key,
                label: tier.label,
                color: tier.color,
                value: tier.value,
                note: tier.key === 't1' ? undefined : pluralize(tier.count, 'клиент', 'клиента', 'клиентов'),
              }))}
            />
          </div>
        )}
      </Panel>

      <Panel
        index={2}
        title="Пять крупнейших клиентов за 12 месяцев"
        subtitle={`Крупнейшие в выбранном периоде · одна шкала на всех · справа — ${
          windows ? `${windows.label} (закончившиеся месяцы с данными)` : 'сравнение 3 к 3 не считается: нет шести закончившихся месяцев с данными подряд'
        } · строка открывает клиента`}
        states={[current, trendState]}
        skeleton={<SkeletonRows rows={5} height={28} />}
        empty={salesEmpty(ctx, top5.length === 0, 'Продаж клиентам за период нет')}
      >
        {() => (
          <div className="trend-rows">
            {top5.map((row, index) => {
              const key = rowKey(row, 'counterparty')
              const map = trendByClient.get(key)
              const year = sumMonths(map, months)
              return (
                <button
                  key={key}
                  type="button"
                  className="trend-rows__row trend-rows__row--button cascade"
                  style={{ '--i': Math.min(index, 7) } as CSSProperties}
                  onClick={(event) => openCustomer(row, event.currentTarget)}
                >
                  <span className="bars__label">
                    <span className="bars__name">{labelOr(row.counterparty, 'Клиент не указан')}</span>
                    <span className="bars__sub">за 12 месяцев {money(year)} · в периоде {money(num(row.revenue))}</span>
                  </span>
                  <MiniColumns
                    ariaLabel={`${labelOr(row.counterparty, 'Клиент')}: выручка по месяцам`}
                    values={months.map((month) => map?.get(month) ?? null)}
                    missing={months.map((month) => missingOf(month, coverage))}
                    labels={months.map(monthLabel)}
                    max={trendMax}
                  />
                  <span className="trend-rows__aside">
                    <Delta
                      value={windows ? deltaPercent(sumMonths(map, windows.recent), sumMonths(map, windows.prior)) : null}
                      label={windows ? undefined : 'нет данных'}
                    />
                  </span>
                </button>
              )
            })}
            <div className="trend-rows__axis" aria-hidden="true">
              <span />
              <span className="mini-columns mini-columns--labels">
                {months.map((month) => (
                  <span
                    key={month}
                    className={`mini-columns__label${rangeMonths.has(month) ? ' mini-columns__label--on' : ''}${missingOf(month, coverage) ? ' mini-columns__label--missing' : ''}`}
                  >
                    {shortMonth(month)}
                  </span>
                ))}
              </span>
              <span />
            </div>
          </div>
        )}
      </Panel>

      <div className="fin-grid fin-grid--half">
        <Panel
          index={3}
          title="Новые клиенты"
          subtitle="Покупали в периоде, не покупали 12 месяцев до него"
          states={[current, history]}
          skeleton={<SkeletonRows rows={4} />}
          empty={
            gap
              ? salesEmpty(ctx, true)
              : historyNarrowed
                ? { when: true, title: 'Не посчитать в ваших правах', hint: 'Права открывают не всю историю за 12 месяцев до периода — новым оказался бы и давний клиент.' }
                : historyGap
                  ? { when: true, title: 'Не посчитать', hint: 'Из 12 месяцев до периода загружены не все — новым оказался бы и давний клиент.' }
                  : { when: fresh.length === 0, title: 'Новых клиентов нет', hint: 'Все покупатели периода покупали и в предыдущие 12 месяцев.' }
          }
        >
          {() => <CustomerList rows={fresh} valueTitle="В периоде" onOpen={openCustomer} />}
        </Panel>
        <Panel
          index={4}
          title="Перестали покупать"
          subtitle={`Покупали в периоде «${previousLabel}», в выбранном — нет · сумма — их выручка тогда`}
          states={[current, before]}
          skeleton={<SkeletonRows rows={4} />}
          empty={
            gap
              ? salesEmpty(ctx, true)
              : lostUnknown
                ? { when: true, title: 'Не посчитать', hint: `Период «${previousLabel}» загружен не целиком — сравнивать не с чем.` }
                : { when: lost.length === 0, title: 'Все прошлые покупатели купили снова', hint: `Каждый, кто покупал в периоде «${previousLabel}», купил и в выбранном.` }
          }
        >
          {() => <CustomerList rows={lost} valueTitle={previousLabel} onOpen={openCustomer} />}
        </Panel>
      </div>

        </>
      )}

      <DataNotes states={[current, history, trendState]} />
    </div>
  )
}

function CustomerList({
  rows,
  valueTitle,
  onOpen,
}: {
  rows: Record<string, unknown>[]
  valueTitle: string
  onOpen: (row: Record<string, unknown>, element: HTMLElement) => void
}) {
  const shown = rows.slice(0, 8)
  const rest = rows.slice(8)
  return (
    <>
      <div className="table-wrap">
        <table className="fin-table fin-table--interactive">
          <thead>
            <tr>
              <th>Клиент</th>
              <th className="num">{valueTitle}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row, index) => (
              <tr
                key={rowKey(row, 'counterparty')}
                className="cascade"
                style={{ '--i': Math.min(index, 7) } as CSSProperties}
                tabIndex={0}
                onClick={(event) => onOpen(row, event.currentTarget)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    onOpen(row, event.currentTarget)
                  }
                }}
              >
                <td className="strong">{labelOr(row.counterparty, 'Клиент не указан')}</td>
                <td className="num mono">{money(num(row.revenue))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rest.length > 0 && (
        <div className="tiny muted" style={{ marginTop: 8 }}>
          И ещё {pluralize(rest.length, 'клиент', 'клиента', 'клиентов')} на {money(rest.reduce((sum, row) => sum + num(row.revenue), 0))}.
        </div>
      )}
    </>
  )
}

